import "./Home.page.scss"
import IconArrowLeft from "@fider/assets/images/heroicons-arrowleft.svg"
import IconLightBulb from "@fider/assets/images/heroicons-light-bulb.svg"
import IconInbox from "@fider/assets/images/heroicons-inbox.svg"

import React, { useEffect, useState, useRef } from "react"
import { Post, Tag, PostStatus } from "@fider/models"
import { Icon, Header, Button, SiteFooter } from "@fider/components"
import { PostsContainer } from "./components/PostsContainer"
import { useFider, usePostOverlay } from "@fider/hooks"
import { ShareFeedback } from "./components/ShareFeedback"
import { HeroBanner } from "./components/HeroBanner"
import { HeroPosters } from "./components/HeroPosters"
import { HERO_VARIANT } from "@fider/services/brand"
import { StatsRow } from "./components/StatsRow"
import { PostCtaCard, TopicsCard, PromoCard, TopPostsCard } from "./components/HomeAside"
import { CtaBand } from "./components/CtaBand"
import { CATEGORY_ICONS } from "./components/categoryIcons"
import { isPostPending, setPostPending } from "./components/PostCache"
import { PostDetails } from "@fider/components/PostDetails"
import { classSet, querystring } from "@fider/services"
import { getCategories, isCategorySlug } from "@fider/services/khcn"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

export interface HomePageProps {
  posts: Post[]
  tags: Tag[]
  searchNoiseWords: string[]
  countPerStatus: { [key: string]: number }
  countPerCategory?: { [slug: string]: number }
}

export interface HomePageState {
  title: string
}

// Trang chủ theo bản thiết kế idea.hatinh-s.vn: hero slider → dải chỉ số → (tab hạng mục + danh sách bài | cột phải)
// → dải kêu gọi → chân trang. Dữ liệu chỉ lấy từ API có sẵn; chức năng cũ (lọc, sắp xếp, tìm kiếm, đăng bài,
// xem bài dạng overlay) giữ nguyên.

// Tab "all" = mọi hạng mục; còn lại là slug hạng mục
const TAB_ALL = "all"

interface EmptyStateProps {
  onNewPost: () => void
  disabled: boolean
  // Khách chưa đăng nhập chỉ xem: không hiện nút đăng bài
  canPost: boolean
}

const EmptyState = (props: EmptyStateProps) => (
  <div className="p-home__empty">
    <span className="p-home__empty-icon">
      <Icon sprite={IconLightBulb} />
    </span>
    <p className="p-home__empty-title">
      <Trans id="khcn.home.empty.title">No ideas yet</Trans>
    </p>
    <p className="p-home__empty-text">
      {props.canPost ? (
        <Trans id="khcn.home.empty.text">Be the first to share your initiative with the community.</Trans>
      ) : (
        <Trans id="khcn.home.empty.guest">Ideas will appear here once the community starts sharing.</Trans>
      )}
    </p>
    {props.canPost && (
      <Button variant="secondary" onClick={props.onNewPost} disabled={props.disabled}>
        <Trans id="khcn.home.empty.action">Share the first idea</Trans>
      </Button>
    )}
  </div>
)

const getInitialTab = (): string => {
  const tags = querystring.getArray("tags")
  const category = tags.find((slug) => isCategorySlug(slug))
  return category || TAB_ALL
}

const getInitialSort = (): string => querystring.get("view") || "trending"

// Dải nút sắp xếp — dùng lại các nhãn có sẵn của Fider (đã dịch)
const sortOptions = () => [
  { value: "trending", label: i18n._({ id: "home.postfilter.option.trending", message: "Trending" }) },
  { value: "recent", label: i18n._({ id: "home.postfilter.option.recent", message: "Recent" }) },
  { value: "most-wanted", label: i18n._({ id: "home.postfilter.option.mostwanted", message: "Most Wanted" }) },
  { value: "most-discussed", label: i18n._({ id: "home.postfilter.option.mostdiscussed", message: "Most Discussed" }) },
]

const SortPills = ({ value, onChange }: { value: string; onChange: (view: string) => void }) => (
  <div className="p-home__sort" role="group" aria-label={i18n._({ id: "home.postsort.label", message: "Sort by:" })}>
    <span className="p-home__sort-label">{i18n._({ id: "home.postsort.label", message: "Sort by:" })}</span>
    {sortOptions().map((option) => (
      <button
        type="button"
        key={option.value}
        className={classSet({ "p-home__sort-pill": true, "p-home__sort-pill--active": value === option.value })}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
)

const HomePage = (props: HomePageProps) => {
  const fider = useFider()
  const postsContainerRef = useRef<PostsContainer>(null)
  const [isShareFeedbackOpen, setIsShareFeedbackOpen] = useState(isPostPending())
  const [activeTab, setActiveTab] = useState(getInitialTab())
  const [sort, setSort] = useState(getInitialSort())
  // Hạng mục chọn từ menu vòng cung, truyền vào form khi mở
  const [presetCategory, setPresetCategory] = useState("")

  const { selectedPostId, handlePostClick, handleCloseOverlay, setIsPostDirty } = usePostOverlay({
    basePath: "/",
    onPostClosed: (postNumber) => postsContainerRef.current?.updateSinglePost(postNumber),
  })

  useEffect(() => {
    // If we're showing the share feedback, make sure we clear the show pending flag (for draft posts)
    if (isShareFeedbackOpen) {
      if (isPostPending()) {
        setPostPending(false)
      }
    }
  })

  const tenant = fider.session.tenant
  const isModalActive = isShareFeedbackOpen && !fider.isReadOnly
  const categories = getCategories()
  const countPerCategory = props.countPerCategory || {}

  const defaultInvitation = i18n._({ id: "khcn.home.defaultinvitation", message: "Have an idea? Share it here..." })

  const counts = props.countPerStatus
  const totalPosts = Object.keys(counts)
    .filter((status) => status !== PostStatus.Deleted.value)
    .reduce((sum, status) => sum + counts[status], 0)
  const isLonely = totalPosts === 0

  // Khách chưa đăng nhập chỉ xem bài
  const canPost = fider.session.isAuthenticated

  const handleNewPost = (categorySlug = "") => {
    setPresetCategory(categorySlug)
    setIsShareFeedbackOpen(true)
  }

  // Mở modal đăng nhập của header (khách bấm "Đăng ý tưởng")
  const openSignIn = () => {
    const button = document.querySelector<HTMLButtonElement>(".c-header__signin")
    if (button) {
      button.click()
    }
  }

  const primaryAction = () => (canPost ? handleNewPost() : openSignIn())

  const scrollToList = () => {
    document.getElementById("danh-sach")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const selectTab = (tab: string) => {
    setActiveTab(tab)
    postsContainerRef.current?.applyCategory(tab === TAB_ALL ? null : tab)
  }

  const selectSort = (view: string) => {
    setSort(view)
    postsContainerRef.current?.applySort(view)
  }

  // Tìm kiếm từ hero banner: áp vào danh sách rồi cuộn tới danh sách
  const handleHeroSearch = (query: string) => {
    postsContainerRef.current?.applySearch(query)
    scrollToList()
  }

  const tabs = [
    { key: TAB_ALL, label: i18n._({ id: "khcn.home.tab.all", message: "All" }), hint: undefined as string | undefined, icon: IconInbox, count: totalPosts },
    ...categories.map((c) => ({
      key: c.slug,
      label: c.label,
      hint: c.hint as string | undefined,
      icon: CATEGORY_ICONS[c.slug],
      count: countPerCategory[c.slug] || 0,
    })),
  ]

  return (
    <>
      <ShareFeedback
        tags={props.tags}
        placeholder={tenant.invitation || defaultInvitation}
        isOpen={isModalActive}
        initialCategory={presetCategory}
        onClose={() => setIsShareFeedbackOpen(false)}
      />
      <div>
        <Header hasInert={isModalActive} />
        {/* Hero tràn toàn chiều rộng, nằm ngoài khung .container; ẩn cùng trang chủ khi đang xem bài dạng overlay */}
        <div className="kh-hero-shell" style={selectedPostId !== null ? { display: "none" } : undefined} {...(isModalActive && { inert: "true" })}>
          {HERO_VARIANT === "posters" ? (
            <HeroPosters
              categories={categories}
              countPerCategory={countPerCategory}
              activeCategory={isCategorySlug(activeTab) ? activeTab : null}
              onSelectCategory={(slug) => selectTab(slug ?? TAB_ALL)}
              onSearch={handleHeroSearch}
            />
          ) : (
            <HeroBanner
              categories={categories}
              countPerCategory={countPerCategory}
              activeCategory={isCategorySlug(activeTab) ? activeTab : null}
              onSelectCategory={(slug) => selectTab(slug ?? TAB_ALL)}
              onSearch={handleHeroSearch}
            />
          )}
        </div>
        <div id="p-home" className="page container" style={selectedPostId !== null ? { display: "none" } : undefined} {...(isModalActive && { inert: "true" })}>
          <StatsRow countPerStatus={props.countPerStatus} />

          <div className="p-home__layout">
            <main className="p-home__main" id="danh-sach">
              <div className="p-home__tabs" role="tablist" aria-label={i18n._({ id: "khcn.home.pillars.label", message: "Post categories" })}>
                {tabs.map((tab) => (
                  <button
                    type="button"
                    key={tab.key}
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    title={tab.hint}
                    className={classSet({ "p-home__tab": true, "p-home__tab--active": activeTab === tab.key })}
                    onClick={() => selectTab(tab.key)}
                  >
                    <span className="p-home__tab-icon">
                      <Icon sprite={tab.icon} />
                    </span>
                    <span className="p-home__tab-label">{tab.label}</span>
                    <span className="p-home__tab-count">{tab.count}</span>
                  </button>
                ))}
              </div>

              {isLonely ? (
                <EmptyState onNewPost={() => handleNewPost()} disabled={fider.isReadOnly} canPost={canPost} />
              ) : (
                <PostsContainer
                  ref={postsContainerRef}
                  posts={props.posts}
                  tags={props.tags}
                  countPerStatus={props.countPerStatus}
                  onPostClick={handlePostClick}
                  controls="minimal"
                  leading={<SortPills value={sort} onChange={selectSort} />}
                />
              )}
            </main>

            <aside className="p-home__aside">
              <PostCtaCard categories={categories} canPost={canPost} disabled={fider.isReadOnly} onSelect={handleNewPost} onSignIn={openSignIn} />
              <TopicsCard
                categories={categories}
                countPerCategory={countPerCategory}
                onSelect={(slug) => {
                  selectTab(slug ?? TAB_ALL)
                  scrollToList()
                }}
              />
              <PromoCard onAction={primaryAction} />
              <TopPostsCard
                posts={props.posts}
                onPostClick={handlePostClick}
                onSeeAll={() => {
                  selectSort("most-wanted")
                  scrollToList()
                }}
              />
            </aside>
          </div>

          <CtaBand onAction={primaryAction} />
        </div>

        {selectedPostId !== null && (
          <div className="page container">
            <button
              type="button"
              className="p-home__back"
              onClick={handleCloseOverlay}
              aria-label={i18n._({ id: "postdetails.backtoall", message: "Back to all suggestions" })}
              title={i18n._({ id: "postdetails.backtoall", message: "Back to all suggestions" })}
            >
              <Icon sprite={IconArrowLeft} />
            </button>
            <PostDetails postNumber={selectedPostId} onDataChanged={() => setIsPostDirty(true)} />
          </div>
        )}

        <SiteFooter />
      </div>
    </>
  )
}

export default HomePage
